import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Bell, BarChart3, Hospital, Settings, LogOut, Pill, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";
import { useAuthStore } from "../../lib/auth";

const navItems = [
  { to: "/dashboard",  icon: LayoutDashboard, label: "ภาพรวม",        section: "main"  },
  { to: "/alerts",    icon: Bell,            label: "การแจ้งเตือน",   section: "main"  },
  { to: "/patients",  icon: Users,           label: "ผู้ป่วย",         section: "main"  },
  { to: "/reminders", icon: Pill,            label: "ตารางยา",         section: "main"  },
  { to: "/reports",   icon: BarChart3,       label: "รายงาน",          section: "tools" },
  { to: "/hospitals", icon: Hospital,        label: "สถานพยาบาล",      section: "tools" },
  { to: "/settings",  icon: Settings,        label: "ตั้งค่า",          section: "tools" },
];

export function Sidebar() {
  const user     = useAuthStore((s) => s.user);
  const logout   = useAuthStore((s) => s.logout);
  const navigate = useNavigate();

  const mainItems  = navItems.filter(i => i.section === "main");
  const toolsItems = navItems.filter(i => i.section === "tools");
  const initials   = user?.name?.slice(0, 2) ?? "??";
  const roleLabel  = user?.role === "doctor" ? "แพทย์" : user?.role === "nurse" ? "พยาบาล" : user?.role ?? "";
  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <aside className="w-64 bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800 flex flex-col h-full shadow-sm shrink-0 transition-colors duration-200">
      <div className="px-5 py-5 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <img src="/tuendee_logo.png" alt="เตือนดี" className="w-9 h-9 rounded-xl object-cover shadow-sm" />
          <div>
            <p className="text-base font-bold text-gray-900 dark:text-white">เตือนดี</p>
            <p className="text-[11px] text-gray-400 dark:text-gray-500 -mt-0.5">TuenDee Health</p>
          </div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        <p className="px-3 mb-2 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">เมนูหลัก</p>
        {mainItems.map(({ to, icon: Icon, label }) => (
          <NavLink key={to} to={to} end
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group",
              isActive
                ? "bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-400"
                : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100"
            )}>
            {({ isActive }) => (
              <>
                <Icon className={cn("w-4 h-4 shrink-0", isActive ? "text-brand-600 dark:text-brand-400" : "text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300")} />
                <span className="flex-1">{label}</span>
                {isActive && <ChevronRight className="w-3.5 h-3.5 text-brand-400 dark:text-brand-500" />}
              </>
            )}
          </NavLink>
        ))}
        <p className="px-3 mt-5 mb-2 text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">เครื่องมือ</p>
        {toolsItems.map(({ to, icon: Icon, label }) => (
          <NavLink key={to} to={to}
            className={({ isActive }) => cn(
              "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group",
              isActive
                ? "bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-400"
                : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100"
            )}>
            {({ isActive }) => (
              <>
                <Icon className={cn("w-4 h-4 shrink-0", isActive ? "text-brand-600 dark:text-brand-400" : "text-gray-400 dark:text-gray-500 group-hover:text-gray-600 dark:group-hover:text-gray-300")} />
                <span>{label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group">
          <div className="w-8 h-8 rounded-full bg-brand-100 dark:bg-brand-900/40 text-brand-700 dark:text-brand-300 text-xs font-semibold flex items-center justify-center shrink-0">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{user?.name ?? "ผู้ใช้งาน"}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 truncate">{roleLabel}</p>
          </div>
          <button onClick={handleLogout} title="ออกจากระบบ"
            className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-300 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 transition-colors shrink-0">
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
